import { getTeamsChannelName, parseTeamsChannelUrl } from "@/utils/teamsChannelUrl";
import { getDocInfoCategoryIds } from "@/utils/conversation";

export type DocumentSourceLike =
  | string
  | {
      id?: string;
      name?: string;
      guidName?: string;
      Guid?: string;
      guid?: string;
      DocumentGuid?: string;
      documentGuid?: string;
      DocumentList_DocumentGuid?: string;
      FileName?: string;
      fileName?: string;
      DocumentList_FileName?: string;
      sourcePathAskId?: string;
      SourcePathAskId?: string;
      DocumentList_SourcePathAskId?: string;
      docCategoryIds?: string[];
      sourcePathAskSourceType?: string;
      SourcePathAskSourceType?: string;
      sourcePathAskDocCategoryIds?: string | string[];
      SourcePathAskDocCategoryIds?: string | string[];
      dataSource?: string;
      DataSource?: string;
      /** 是否在 DB DocumentList 中存在；右侧边栏仅展示该标记为 true 的文档 */
      inDocumentList?: boolean;
    };

const normalizeText = (value: unknown): string => {
  if (typeof value !== "string" && typeof value !== "number") return "";
  return String(value).trim();
};

type DocumentDataSourceLinkType = "teams" | "sharepoint";

export interface DocumentDataSourceLink {
  url: string;
  type: DocumentDataSourceLinkType;
}

export const buildAddSourcesSearchParams = ({
  page,
  limit,
  keyword,
  docCategoryIds,
  studySourceIds,
  includePersonalDocs = true,
}: {
  page: number;
  limit: number;
  keyword?: string;
  docCategoryIds?: string[];
  studySourceIds?: string[];
  includePersonalDocs?: boolean;
}): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    Page: page,
    Limit: limit,
    // 筛选配置尚未加载时不传 General / Study，兼容原有“全部授权来源”行为。
    IncludePersonalDocs: includePersonalDocs,
  };
  const normalizedKeyword = keyword?.trim();
  if (normalizedKeyword) params.FileName = normalizedKeyword;
  if (docCategoryIds !== undefined) params.DocCategoryIds = docCategoryIds;
  if (studySourceIds !== undefined) params.StudySourceIds = studySourceIds;
  return params;
};

const parseHttpUrl = (value: unknown): URL | null => {
  const text = normalizeText(value);
  if (!text) return null;

  try {
    const url = new URL(text);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
};

const isSharePointHost = (hostname: string): boolean => hostname.endsWith(".sharepoint.com");

const getSharePointSiteUrl = (value: unknown): string => {
  const url = parseHttpUrl(value);
  if (!url || !isSharePointHost(url.hostname.toLowerCase())) return "";

  const segments = url.pathname.split("/").filter(Boolean);
  const siteRootIndex = segments.findIndex((segment) => {
    const normalized = segment.toLowerCase();
    return normalized === "sites" || normalized === "teams";
  });

  url.search = "";
  url.hash = "";
  url.pathname =
    siteRootIndex >= 0 && segments[siteRootIndex + 1]
      ? `/${segments.slice(0, siteRootIndex + 2).join("/")}`
      : "/";
  return url.toString().replace(/\/$/, "");
};

/** Add Sources 来源列：Teams Channel URL 显示 Channel Name，其他来源保持原值。 */
export const getDocumentDataSourceLabel = (value: unknown): string => {
  const dataSource = normalizeText(value);
  if (!dataSource) return "";
  return getTeamsChannelName(dataSource) || dataSource;
};

/** Add Sources 来源跳转：Teams 打开 Channel，SharePoint 文件地址归一到对应 Site 根地址。 */
export const getDocumentDataSourceLink = (
  dataSourceValue: unknown,
  fileSrcUrlValue: unknown,
): DocumentDataSourceLink | null => {
  const dataSourceUrl = parseHttpUrl(dataSourceValue);
  if (dataSourceUrl && parseTeamsChannelUrl(dataSourceUrl.toString())) {
    return { url: dataSourceUrl.toString(), type: "teams" };
  }

  const sharePointSiteUrl =
    getSharePointSiteUrl(dataSourceValue) || getSharePointSiteUrl(fileSrcUrlValue);
  return sharePointSiteUrl ? { url: sharePointSiteUrl, type: "sharepoint" } : null;
};

export const getDocumentName = (doc: DocumentSourceLike): string => {
  if (!doc || typeof doc === "string") return typeof doc === "string" ? doc : "";
  return (
    normalizeText(doc.name) ||
    normalizeText(doc.FileName) ||
    normalizeText(doc.fileName) ||
    normalizeText(doc.DocumentList_FileName)
  );
};

export const getDocumentGuid = (doc: DocumentSourceLike | null | undefined): string => {
  if (!doc || typeof doc === "string") return "";
  return (
    normalizeText(doc.guidName) ||
    normalizeText(doc.id) ||
    normalizeText(doc.documentGuid) ||
    normalizeText(doc.DocumentGuid) ||
    normalizeText(doc.DocumentList_DocumentGuid) ||
    normalizeText(doc.Guid) ||
    normalizeText(doc.guid)
  );
};

export const getDocumentSourcePathAskId = (doc: DocumentSourceLike | null | undefined): string => {
  if (!doc || typeof doc === "string") return "";
  return (
    normalizeText(doc.sourcePathAskId) ||
    normalizeText(doc.SourcePathAskId) ||
    normalizeText(doc.DocumentList_SourcePathAskId)
  );
};

export const getDocumentDataSource = (doc: DocumentSourceLike | null | undefined): string => {
  if (!doc || typeof doc === "string") return "";
  return normalizeText(doc.dataSource) || normalizeText(doc.DataSource);
};

export const getDocumentCategoryIds = (doc: DocumentSourceLike | null | undefined): string[] => {
  if (!doc || typeof doc === "string") return [];
  return getDocInfoCategoryIds(doc);
};

export const getDocumentDedupKey = (doc: DocumentSourceLike | null | undefined): string => {
  // 按 DocumentGuid 去重：右侧文档清单完全以 getdocumentid 校准回的 DocumentGuid 为准
  // （命中 DB 文档的 doc 已把 DocumentGuid 写入 guidName，getDocumentGuid 优先取它）。
  const guid = getDocumentGuid(doc);
  if (guid) return `guid:${guid.toLowerCase()}`;
  return `name:${getDocumentName(doc)}`;
};

/** 文档是否在 DB DocumentList 中存在（仅此类文档进入右侧边栏的可选清单） */
export const isInDocumentList = (doc: DocumentSourceLike | null | undefined): boolean => {
  if (!doc || typeof doc === "string") return false;
  return doc.inDocumentList === true;
};

export const isValidDocumentSource = (doc: unknown): doc is DocumentSourceLike => {
  if (typeof doc === "string") return doc.trim().length > 0;
  if (typeof doc !== "object" || doc === null) return false;
  return getDocumentName(doc as DocumentSourceLike).length > 0;
};

export const collectSessionDocuments = (items: unknown[]): DocumentSourceLike[] => {
  const map = new Map<string, DocumentSourceLike>();
  (Array.isArray(items) ? items : []).forEach((item: any) => {
    const docs = item?.documents;
    if (!Array.isArray(docs)) return;
    docs.forEach((doc: unknown) => {
      if (!isValidDocumentSource(doc)) return;
      const key = getDocumentDedupKey(doc);
      if (!map.has(key)) map.set(key, doc);
    });
  });
  return Array.from(map.values());
};

export const buildDisplayDocuments = ({
  latestDocs,
  sessionDocs,
  manualSources,
}: {
  latestDocs: DocumentSourceLike[];
  sessionDocs: DocumentSourceLike[];
  manualSources: DocumentSourceLike[];
}): DocumentSourceLike[] => {
  const map = new Map<string, DocumentSourceLike>();

  latestDocs.forEach((doc) => {
    map.set(getDocumentDedupKey(doc), doc);
  });
  sessionDocs.forEach((doc) => {
    const key = getDocumentDedupKey(doc);
    if (!map.has(key)) map.set(key, doc);
  });
  manualSources.forEach((doc) => {
    const key = getDocumentDedupKey(doc);
    if (!map.has(key)) map.set(key, doc);
  });

  return Array.from(map.values());
};
